import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { FC, Fragment } from 'react';
import useWindowResizer from '@/shared/hooks/window-resizer/window-resizer.hook';
import BackIcon from '@/assets/svgs/sub-header-back.icon.svg?react';
import { CustomButtonProps } from '@/shared/components/core-ui/button/button.component';
import Chip, { ChipProps } from '@/shared/components/core-ui/chip/chip.component';
import { useLocalization } from '@/shared/hooks/useLocalization';

import {
  StyledButton,
  StyledContainer,
  StyledMainContainer,
  StyledSkeleton,
  StyledTab,
  StyledTabs,
  StyledTabsContainer
} from './page-header.styles';

interface PageHeaderProps {
  data: {
    title: string;
    titleImage?: string;
    description?: string;
    loading?: boolean;
    selectedTab?: string | boolean;
    anchorEl?: null | HTMLElement;
    buttonMenuItems?: ButtonMenuItem[];
    tabsLoading?: boolean;
    rejectionAnchorEl?: null | HTMLElement;
    rejectionMenuItems?: ButtonMenuItem[];
    exportAnchorEl?: null | HTMLElement;
    exportMenuItems?: ButtonMenuItem[];
  };
  config?: { hasBackNavigation?: boolean; colleace?: boolean };
  components?: {
    chipList?: ChipProps[];
    actionsList: CustomButtonProps[];
    tabs?: {
      value: string;
      label: string;
      disabled?: boolean;
    }[];
  };
  eventHandlers?: {
    handleBackNavigation?: () => void;
    setSelectedTab?: (value: string) => void;
    setAnchorEl?: (anchorEl: null | HTMLElement) => void;
    setRejectionAnchorEl?: (anchorEl: null | HTMLElement) => void;
    setExportAnchorEl?: (anchorEl: null | HTMLElement) => void;
  };
}

export interface ButtonMenuItem {
  name: string;
  action: () => void;
}

const PageHeader: FC<PageHeaderProps> = ({ data, config = {}, components, eventHandlers }) => {
  const {
    title,
    titleImage = '',
    description,
    loading,
    selectedTab,
    anchorEl,
    buttonMenuItems,
    tabsLoading = false,
    rejectionAnchorEl,
    rejectionMenuItems,
    exportAnchorEl,
    exportMenuItems
  } = data;
  const { hasBackNavigation, colleace = false } = config;
  const { chipList, actionsList, tabs } = components ?? { actionsList: [] };
  const {
    handleBackNavigation,
    setSelectedTab,
    setAnchorEl,
    setRejectionAnchorEl,
    setExportAnchorEl
  } = eventHandlers ?? {};

  const isMobileView = useWindowResizer();
  const theme = useTheme();
  const { isRTL } = useLocalization();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab?.(newValue);
  };

  const handleClose = () => {
    setAnchorEl?.(null);
  };

  const handleRejectionClose = () => {
    setRejectionAnchorEl?.(null);
  };

  const handleExportClose = () => {
    setExportAnchorEl?.(null);
  };

  const open = Boolean(anchorEl);
  const rejectionOpen = Boolean(rejectionAnchorEl);
  const exportOpen = Boolean(exportAnchorEl);

  return (
    <StyledMainContainer>
      <StyledContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '50%', flex: 1 }}>
          {hasBackNavigation && (
            <IconButton onClick={handleBackNavigation}>
              <BackIcon style={{ transform: isRTL ? 'scaleX(-1)' : 'scaleX(1)' }} />
            </IconButton>
          )}
          {loading ? (
            <StyledSkeleton variant="rounded" />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: titleImage ? 'column' : 'row'
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: titleImage ? '16px' : 0
                  }}
                >
                  {titleImage && (
                    <Avatar
                      variant="circular"
                      sx={{
                        width: '48px',
                        height: '48px',
                        marginRight: '16px',
                        background: 'transparent'
                      }}
                      src={titleImage}
                    />
                  )}
                  <Typography
                    sx={{
                      ml: hasBackNavigation && !titleImage ? '8px' : 0
                    }}
                    component="h1"
                  >
                    {title}
                  </Typography>
                </Box>
                <Typography component="p">{description}</Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexFlow: 'wrap',
                  gap: '3px 0'
                }}
              >
                {chipList?.map((chip, index) => {
                  return (
                    <Chip
                      key={index}
                      data={{
                        label: chip.data.label,
                        image: chip.data.image,
                        labelValueText: chip.data.labelValueText
                      }}
                      config={{
                        isDisabled: chip.config.isDisabled,
                        chipColor: chip.config.chipColor
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {actionsList && actionsList.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              maxWidth: '50%',
              flex: 1,
              flexFlow: 'wrap',
              justifyContent: 'flex-end'
            }}
          >
            {actionsList.map((action, index) => {
              return (
                <Fragment key={index}>
                  <StyledButton
                    onClick={action.eventHandlers.handleClick}
                    color={action.config.color}
                    size={action.config.size}
                    variant={action.config.variant}
                    startIcon={action.config.startIcon}
                    endIcon={action.config.endIcon}
                    disabled={action.config.disabled}
                    loading={action.config.loading}
                  >
                    {action.children}
                  </StyledButton>

                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      'aria-labelledby': 'basic-button'
                    }}
                  >
                    {buttonMenuItems?.map((menuItem) => {
                      return (
                        <MenuItem key={menuItem.name} onClick={menuItem.action}>
                          {menuItem.name}
                        </MenuItem>
                      );
                    })}
                  </Menu>

                  <Menu
                    id="rejection-menu"
                    anchorEl={rejectionAnchorEl}
                    open={rejectionOpen}
                    onClose={handleRejectionClose}
                    MenuListProps={{
                      'aria-labelledby': 'rejection-button'
                    }}
                  >
                    {rejectionMenuItems?.map((menuItem) => {
                      return (
                        <MenuItem key={menuItem.name} onClick={menuItem.action}>
                          {menuItem.name}
                        </MenuItem>
                      );
                    })}
                  </Menu>

                  <Menu
                    id="export-menu"
                    anchorEl={exportAnchorEl}
                    open={exportOpen}
                    onClose={handleExportClose}
                    MenuListProps={{
                      'aria-labelledby': 'export-button'
                    }}
                  >
                    {exportMenuItems?.map((menuItem) => {
                      return (
                        <MenuItem key={menuItem.name} onClick={menuItem.action}>
                          {menuItem.name}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Fragment>
              );
            })}
          </Box>
        )}
      </StyledContainer>

      {tabs?.length === 0 && !tabsLoading && <Box sx={{ height: '78px' }} />}

      {tabsLoading ? (
        <Box sx={{ display: 'flex' }}>
          <Skeleton
            variant="text"
            width={'20%'}
            height={78}
            sx={{ bgcolor: theme.palette.action.hover }}
          />
        </Box>
      ) : (
        tabs && (
          <StyledTabsContainer>
            <StyledTabs
              sx={{ marginLeft: colleace || isMobileView ? 0 : '32px' }}
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {tabs.map((tab) => {
                return (
                  <StyledTab
                    sx={{ ...(!colleace && { minWidth: '120px', marginRight: '40px' }) }}
                    key={tab.value}
                    value={tab.value}
                    label={tab.label}
                    disabled={tab.disabled}
                  />
                );
              })}
            </StyledTabs>
          </StyledTabsContainer>
        )
      )}
    </StyledMainContainer>
  );
};

export default PageHeader;
